'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { CourtFeed } from '@/app/[locale]/event/[eventId]/libs/eventFeeds';
import { Chip } from '@/components/ui/Chip';
import { StreamPlayer } from '@/components/streaming/StreamPlayer';

type Translate = ReturnType<typeof useTranslations<'EventWatch'>>;

/** A camera's name, or "Camera n" when the feed has none. */
export const cameraLabel = (t: Translate, feed: CourtFeed, index: number) =>
  feed.cameraName ?? t('cameraN', { n: index + 1 });

/**
 * The live player for a match in progress, with one chip per camera when the court has several.
 * It carries no heading: the scorecard page puts it first, and `MatchFeeds` adds "Live now" above
 * it on the futsal page.
 */
export default function MatchLiveFeeds({ feeds }: { feeds: CourtFeed[] }) {
  const t = useTranslations('EventWatch');
  const [pickedId, setPickedId] = useState<string | null>(null);
  if (feeds.length === 0) return null;

  const chosen = feeds.find((f) => f.id === pickedId) ?? feeds[0];
  const chosenIndex = feeds.indexOf(chosen);
  return (
    <div data-match-live-feeds className="mx-auto flex w-full max-w-3xl min-w-0 flex-col gap-2">
      <StreamPlayer key={chosen.id} feed={chosen} title={cameraLabel(t, chosen, chosenIndex)} />
      {feeds.length > 1 && (
        <div role="group" aria-label={t('cameras')} className="flex flex-wrap gap-2">
          {feeds.map((feed, i) => (
            <Chip
              key={feed.id}
              preserveCase
              // Chip is nowrap and shrink-0; a long camera name must wrap inside a 320px viewport.
              className="max-w-full whitespace-normal"
              selected={feed.id === chosen.id}
              onClick={() => setPickedId(feed.id)}
            >
              {cameraLabel(t, feed, i)}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}
